import {
  createContext,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useContext,
} from 'react';

import { type CapturedChart, ShareCaptureScope } from '@revelio/replots';

import type { PositionedItem } from '../PageConfigProvider/types/item';
import {
  type ReplotsChartName,
  useSharedReportData,
} from '../SharedReport/SharedReportDataContext';

interface ChartContextValue {
  item: PositionedItem;
  variant: 'default' | 'report';
  /** Opens chart builder when a chart card is clicked in edit mode. */
  onEditClick?: MouseEventHandler<HTMLDivElement>;
}

const ChartContext = createContext<ChartContextValue | undefined>(undefined);

interface ChartProviderProps {
  item: PositionedItem;
  variant?: 'default' | 'report';
  onEditClick?: MouseEventHandler<HTMLDivElement>;
  children: ReactNode;
}

export const ChartProvider = ({
  item,
  variant,
  onEditClick,
  children,
}: ChartProviderProps) => {
  const sharedReportData = useSharedReportData();
  const register = useCallback(
    (scopeKey: string, capture: CapturedChart) => {
      sharedReportData?.updateChartData(scopeKey, {
        component: capture.component as ReplotsChartName,
        props: capture.props,
      });
    },
    [sharedReportData]
  );

  const content = (
    <ChartContext.Provider
      value={{ item, variant: variant ?? 'default', onEditClick }}
    >
      {children}
    </ChartContext.Provider>
  );

  // Skip the scope when no share session is active so `withShareCapture`
  // becomes a no-op and avoids the per-render prop stringify.
  if (!sharedReportData) return content;

  return (
    <ShareCaptureScope scopeKey={item.id} register={register}>
      {content}
    </ShareCaptureScope>
  );
};

export const useChart = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a ChartProvider');
  }
  return context;
};

export const useSafeChart = () => {
  const context = useContext(ChartContext);
  if (!context) {
    return {
      item: undefined,
      variant: 'default' as const,
      onEditClick: undefined,
    };
  }
  return context;
};
